// @ts-nocheck
import React from 'react';

import Btn from '../claims/shared/Btn';
import Chip from '../claims/shared/Chip';

/**
 * approval_status → Chip tone/label mapping (UI-SPEC §3). `failed` covers the
 * generation-failed surface; no new Chip tones are introduced.
 */
const STATUS_CHIP = {
  draft: { tone: 'draft', label: 'Draft' },
  provider_review: { tone: 'pending', label: 'In review' },
  approved: { tone: 'approved', label: 'Approved' },
  pdf_generated: { tone: 'approved', label: 'PDF ready' },
  failed: { tone: 'denied', label: 'Failed' },
};

function formatApprovedDate(iso) {
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return '';
  }
}

/**
 * Persistent bar above the section list (UI-SPEC §3): left chip + version
 * caption, right action buttons. Never two primary (black) buttons at once —
 * Download final PDF replaces Approve letter in pdf_generated.
 *
 * @param {{
 *   letter: object|null,
 *   sectionsCount?: number,
 *   sectionsSavingCount?: number,
 *   approving?: boolean,
 *   onPreview?: () => void,
 *   onApprove?: () => void,
 *   onDownload?: () => void,
 * }} props
 */
export default function LetterActionBar({
  letter,
  sectionsCount = 0,
  sectionsSavingCount = 0,
  approving = false,
  onPreview,
  onApprove,
  onDownload,
}) {
  const status = letter?.approval_status || 'draft';
  const chip = approving
    ? { tone: 'pending', label: 'Rendering PDF…' }
    : STATUS_CHIP[status] || STATUS_CHIP.draft;

  const isPdfReady = status === 'pdf_generated';
  const isApproved = status === 'approved' || isPdfReady;
  const version = letter?.version;
  const caption =
    version == null
      ? ''
      : isApproved && letter?.approved_at
        ? `Version ${version} · Approved ${formatApprovedDate(letter.approved_at)}`
        : `Version ${version}`;

  const approveEnabled =
    (status === 'draft' || status === 'provider_review') &&
    sectionsSavingCount === 0 &&
    !approving;

  return (
    <div
      className="ab"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: 16,
        background: 'var(--ab-bg)',
        border: 'var(--ab-hairline)',
        borderRadius: 'var(--ab-radius-md)',
      }}
    >
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 12 }}>
        <Chip tone={chip.tone}>{chip.label}</Chip>
        {caption && (
          <span style={{ fontSize: 'var(--ab-text-xs)', color: 'var(--ab-fg-subtle)' }}>
            {caption}
          </span>
        )}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Btn
          kind="secondary"
          icon="file-text"
          onClick={onPreview}
          disabled={sectionsCount < 1}
        >
          Preview PDF
        </Btn>
        {!isPdfReady && (
          <Btn kind="primary" icon="check" onClick={onApprove} disabled={!approveEnabled}>
            Approve letter
          </Btn>
        )}
        {isPdfReady && (
          <Btn kind="primary" icon="download" onClick={onDownload}>
            Download final PDF
          </Btn>
        )}
      </div>
    </div>
  );
}
